import React, { useState } from 'react';
import { X } from 'lucide-react';
import { CalendarEvent } from '../../types/calendar';
import { format, addHours } from 'date-fns';
import SimpleDatePicker from './SimpleDatePicker';
import SimpleTimePicker from './SimpleTimePicker';

interface CreateEventModalProps {
  onClose: () => void;
  onCreate: (event: Partial<CalendarEvent>) => void;
  initialDate?: Date | null;
}

export default function CreateEventModal({ onClose, onCreate, initialDate }: CreateEventModalProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [allDay, setAllDay] = useState(false);
  const [type, setType] = useState<CalendarEvent['type']>('event');
  const [visibility, setVisibility] = useState<CalendarEvent['visibility']>('public');
  const [reminderTime, setReminderTime] = useState(30);
  const [loading, setLoading] = useState(false);

  // Smart defaults for date and time
  const getDefaultDate = () => {
    if (initialDate) {
      return format(initialDate, 'yyyy-MM-dd');
    }
    return format(new Date(), 'yyyy-MM-dd');
  };

  const getDefaultStartTime = () => {
    if (initialDate) {
      return format(initialDate, 'HH:mm');
    }
    const now = new Date();
    now.setMinutes(0, 0, 0);
    now.setHours(now.getHours() + 1);
    return format(now, 'HH:mm');
  };

  const getDefaultEndTime = () => {
    if (initialDate) {
      return format(addHours(initialDate, 1), 'HH:mm');
    }
    const now = new Date();
    now.setMinutes(0, 0, 0);
    now.setHours(now.getHours() + 2);
    return format(now, 'HH:mm');
  };

  const [startDate, setStartDate] = useState(getDefaultDate());
  const [startTime, setStartTime] = useState(getDefaultStartTime());
  const [endDate, setEndDate] = useState(getDefaultDate());
  const [endTime, setEndTime] = useState(getDefaultEndTime());

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim()) {
      return;
    }

    setLoading(true);
    
    const start = allDay ? `${startDate}T00:00` : `${startDate}T${startTime}`;
    const end = allDay ? `${endDate}T23:59` : `${endDate}T${endTime}`;

    await onCreate({
      title: title.trim(),
      description: description.trim(),
      start,
      end,
      type,
      visibility,
      location: location.trim(),
      allDay,
      reminderTime,
    });
    
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-md max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Create Event</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-8rem)]">
          {/* Title */}
          <div>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Event title"
              className="w-full px-4 py-3 text-lg rounded-lg border border-gray-200 
                       focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 
                       outline-none transition-all"
              required
            />
          </div>

          {/* All Day Toggle */}
          <div className="flex items-center justify-between py-2">
            <span className="text-gray-700 font-medium">All-day</span>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={allDay}
                onChange={(e) => setAllDay(e.target.checked)}
                className="sr-only peer"
              />
              <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 
                            peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full 
                            peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] 
                            after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 
                            after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          {/* Start Date/Time */}
          <div className="space-y-4">
            <div className="flex items-center space-x-4">
              <span className="text-gray-700 font-medium w-16">Starts</span>
              <div className="flex-1">
                <SimpleDatePicker
                  value={startDate}
                  onChange={(date) => {
                    setStartDate(date);
                    // Auto-set end date to same day if not set
                    if (!endDate || endDate === startDate) {
                      setEndDate(date);
                    }
                  }}
                />
              </div>
              {!allDay && (
                <div className="w-32">
                  <button
                    type="button"
                    className="w-full px-3 py-3 text-center rounded-lg border border-gray-200 
                             hover:border-blue-300 focus:border-blue-500 focus:ring-2 
                             focus:ring-blue-500/20 outline-none transition-all bg-white"
                    onClick={(e) => {
                      const button = e.currentTarget;
                      const rect = button.getBoundingClientRect();
                      const dropdown = button.nextElementSibling as HTMLElement;
                      if (dropdown) {
                        dropdown.style.display = dropdown.style.display === 'block' ? 'none' : 'block';
                        dropdown.style.top = `${rect.bottom + 8}px`;
                        dropdown.style.left = `${rect.left}px`;
                      }
                    }}
                  >
                    <span className="text-red-500 font-medium">
                      {(() => {
                        const [hours, minutes] = startTime.split(':').map(Number);
                        const period = hours >= 12 ? 'PM' : 'AM';
                        const displayHour = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
                        return `${displayHour}:${minutes.toString().padStart(2, '0')} ${period}`;
                      })()}
                    </span>
                  </button>
                  <div
                    className="fixed bg-white border border-gray-200 rounded-lg shadow-lg z-50 hidden"
                    style={{ width: '300px' }}
                  >
                    <SimpleTimePicker
                      value={startTime}
                      onChange={setStartTime}
                    />
                    <div className="p-4 border-t border-gray-200">
                      <button
                        type="button"
                        onClick={(e) => {
                          const dropdown = e.currentTarget.closest('div');
                          if (dropdown) dropdown.style.display = 'none';
                        }}
                        className="w-full px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                      >
                        Done
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* End Date/Time */}
            <div className="flex items-center space-x-4">
              <span className="text-gray-700 font-medium w-16">Ends</span>
              <div className="flex-1">
                <SimpleDatePicker
                  value={endDate}
                  onChange={setEndDate}
                />
              </div>
              {!allDay && (
                <div className="w-32">
                  <button
                    type="button"
                    className="w-full px-3 py-3 text-center rounded-lg border border-gray-200 
                             hover:border-blue-300 focus:border-blue-500 focus:ring-2 
                             focus:ring-blue-500/20 outline-none transition-all bg-white"
                    onClick={(e) => {
                      const button = e.currentTarget;
                      const rect = button.getBoundingClientRect();
                      const dropdown = button.nextElementSibling as HTMLElement;
                      if (dropdown) {
                        dropdown.style.display = dropdown.style.display === 'block' ? 'none' : 'block';
                        dropdown.style.top = `${rect.bottom + 8}px`;
                        dropdown.style.left = `${rect.left}px`;
                      }
                    }}
                  >
                    <span className="text-red-500 font-medium">
                      {(() => {
                        const [hours, minutes] = endTime.split(':').map(Number);
                        const period = hours >= 12 ? 'PM' : 'AM';
                        const displayHour = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
                        return `${displayHour}:${minutes.toString().padStart(2, '0')} ${period}`;
                      })()}
                    </span>
                  </button>
                  <div
                    className="fixed bg-white border border-gray-200 rounded-lg shadow-lg z-50 hidden"
                    style={{ width: '300px' }}
                  >
                    <SimpleTimePicker
                      value={endTime}
                      onChange={setEndTime}
                    />
                    <div className="p-4 border-t border-gray-200">
                      <button
                        type="button"
                        onClick={(e) => {
                          const dropdown = e.currentTarget.closest('div');
                          if (dropdown) dropdown.style.display = 'none';
                        }}
                        className="w-full px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                      >
                        Done
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Location */}
          <div>
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Location"
              className="w-full px-4 py-3 rounded-lg border border-gray-200 
                       focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 
                       outline-none transition-all"
            />
          </div>

          {/* Description */}
          <div>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Description"
              rows={3}
              className="w-full px-4 py-3 rounded-lg border border-gray-200 
                       focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 
                       outline-none transition-all resize-none"
            />
          </div>

          {/* Type and Visibility */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value as CalendarEvent['type'])}
                className="w-full px-4 py-3 rounded-lg border border-gray-200 
                         focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 
                         outline-none transition-all"
              >
                <option value="event">Event</option>
                <option value="task">Task</option>
                <option value="reminder">Reminder</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Visibility</label>
              <select
                value={visibility}
                onChange={(e) => setVisibility(e.target.value as CalendarEvent['visibility'])}
                className="w-full px-4 py-3 rounded-lg border border-gray-200 
                         focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 
                         outline-none transition-all"
              >
                <option value="public">Public</option>
                <option value="private">Private</option>
              </select>
            </div>
          </div>

          {/* Reminder */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Reminder</label>
            <select
              value={reminderTime}
              onChange={(e) => setReminderTime(Number(e.target.value))}
              className="w-full px-4 py-3 rounded-lg border border-gray-200 
                       focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 
                       outline-none transition-all"
            >
              <option value={0}>No reminder</option>
              <option value={5}>5 minutes before</option>
              <option value={15}>15 minutes before</option>
              <option value={30}>30 minutes before</option>
              <option value={60}>1 hour before</option>
              <option value={1440}>1 day before</option>
            </select>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !title.trim()}
              className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 
                       transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating...' : 'Create Event'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}